import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';

import {
  COMMON,
  COMMUNITIES_COMMON,
  DIALOG_TITLE,
} from '../../../constants/strings';
import { InviteToCommunityModal } from '../InviteToCommunityModal';
import ActionConfirmation from '../../common/ActionConfirmation';
import CopyToClipboard from '../../common/CopyToClipboard';
import EditCommunityForm from '../EditCommunityForm';
import { useToggleFlag } from '../../../hocs/useToogleFlag';
import IconMenu from '../../common/IconMenu';
import Popup from '../../common/Popup';
import { IDS } from '../../../constants/ids';
import { enhance } from './enhance';

const CommunityMenuImpl = ({
  leaveCommunity,
  updateCommunityInfo,
  showCommunityReport,
  intl: { formatMessage },
  getMenuItems,
  isModerator,
  community: {
    name,
    description,
    currentUserRole,
    id: communityId,
    linkSharing,
    sharedLink,
  } = {},
}) => {
  const [isSearchOpen, openSearch, closeSearch] = useToggleFlag();
  const [isLeaveOpen, openLeave, closeLeave] = useToggleFlag();
  const [isSharingOpen, openSharing, closeSharing] = useToggleFlag();
  const [isEditOpen, openEdit, closeEdit] = useToggleFlag();

  const handleLeavePopup = useCallback(() => {
    leaveCommunity({ communityId });
    closeLeave();
  }, [communityId]);

  const handleUpdateCommunity = useCallback(
    data => {
      updateCommunityInfo({ communityId, ...data });
      closeEdit();
    },
    [communityId],
  );

  const openReportModal = useCallback(() => showCommunityReport(communityId), [
    communityId,
  ]);

  const menuItems = {
    editCommunity: isModerator ? openEdit : null,
    inviteToCommunity: isModerator ? openSearch : null,
    shareLink: linkSharing && currentUserRole ? openSharing : null,
    report: openReportModal,
    leaveCommunity: currentUserRole ? openLeave : null,
  };

  return (
    <Fragment>
      <IconMenu
        id={IDS.COMMUNITY.MENU_TRIGGER}
        position={{ horizontal: 'right', vertical: 'bottom' }}
        items={getMenuItems(menuItems)}
        className="mr2 ml2"
        allScreens
      />
      {/*  U S E A R    S E A R C H    M O D A L */}
      <If condition={isModerator}>
        <InviteToCommunityModal
          communityId={communityId}
          isOpen={isSearchOpen}
          onClose={closeSearch}
        />
      </If>
      {/*  C O N F I R M A T I O N    M O D A L */}
      <If condition={currentUserRole}>
        <ActionConfirmation
          onClose={closeLeave}
          onSubmit={handleLeavePopup}
          opened={isLeaveOpen}
          title={formatMessage({ id: 'community.menu.leaveCommunity' })}
          body={formatMessage({ id: 'community.menu.leaveMessage' })}
          actionButtonText={formatMessage({ id: 'community.menu.leave' })}
          width="xsmall"
          mobileFullScreen={false}
        />
      </If>
      {/*  S H A R I N G    M O D A L */}
      <If condition={linkSharing && currentUserRole}>
        <Popup
          title={formatMessage(COMMUNITIES_COMMON.share)}
          mobileFullScreen={false}
          opened={isSharingOpen}
          onClose={closeSharing}
          strictClose
          width="small"
        >
          <CopyToClipboard
            buttonText={formatMessage(COMMON.copy)}
            copySuccessText={formatMessage(DIALOG_TITLE.copiedToClipboard)}
            title={formatMessage(DIALOG_TITLE.shareableLink)}
            link={sharedLink}
          />
        </Popup>
      </If>
      {/*  E D I T    C O M M U N I T Y    M O D A L */}
      <If condition={isModerator}>
        <Popup
          title={formatMessage({ id: 'community.menu.editCommunity' })}
          mobileFullScreen
          opened={isEditOpen}
          onClose={closeEdit}
          strictClose
          width="small"
        >
          <EditCommunityForm
            onSubmit={handleUpdateCommunity}
            formatMessage={formatMessage}
            name={name}
            onCancel={closeEdit}
            description={description}
          />
        </Popup>
      </If>
    </Fragment>
  );
};

export const CommunityMenu = enhance(CommunityMenuImpl);

const propTypes = {
  community: PropTypes.object.isRequired,
};
CommunityMenuImpl.propTypes = {
  leaveCommunity: PropTypes.func.isRequired,
  updateCommunityInfo: PropTypes.func.isRequired,
  showCommunityReport: PropTypes.func.isRequired,
  getMenuItems: PropTypes.func.isRequired,
  isModerator: PropTypes.bool.isRequired,
  community: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  ...propTypes,
};

CommunityMenu.propTypes = propTypes;



// WEBPACK FOOTER //
// ./src/components/Community/CommunityMenu/index.jsx